import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Package, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Flag, RefreshCw, Printer, Clock, Check, CalendarDays } from "lucide-react";
import { useCompanyLogo } from "@/hooks/use-company-logo";
import type { OrderWithItems, ProductionSession, ProductTotals } from "@shared/schema";

const REFETCH_INTERVAL = 5000;

export default function PakarePage() {
  const { toast } = useToast();
  const logoUrl = useCompanyLogo();
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [packerId, setPackerId] = useState<string>(() => {
    return localStorage.getItem("pakareId") || "";
  });

  const { data: production, isFetching: productionFetching } = useQuery<ProductionSession | null>({
    queryKey: ["/api/production/active"],
    refetchInterval: REFETCH_INTERVAL,
  });

  const { data: orders, isLoading, isFetching: ordersFetching } = useQuery<OrderWithItems[]>({
    queryKey: ["/api/production/orders"],
    enabled: !!production,
    refetchInterval: REFETCH_INTERVAL,
  });

  const { data: totals, isFetching: totalsFetching } = useQuery<ProductTotals[]>({
    queryKey: ["/api/production/totals"],
    enabled: !!production,
    refetchInterval: REFETCH_INTERVAL,
  });

  const isRefreshing = productionFetching || ordersFetching || totalsFetching;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/production/active"] });
    queryClient.invalidateQueries({ queryKey: ["/api/production/orders"] });
    queryClient.invalidateQueries({ queryKey: ["/api/production/totals"] });
  };

  const handlePackerIdChange = (value: string) => {
    setPackerId(value);
    localStorage.setItem("pakareId", value);
  };

  const markPackedMutation = useMutation({
    mutationFn: (orderId: string) => apiRequest("POST", `/api/production/orders/${orderId}/pack`, { packedBy: packerId }),
    onSuccess: () => {
      toast({ title: "Order markerad som packad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/production/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera order.", variant: "destructive" });
    },
  });

  const finishProductionMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/production/finish"),
    onSuccess: () => {
      toast({ title: "Produktion avslutad!", description: "Alla ordrar är levererade." });
      queryClient.invalidateQueries({ queryKey: ["/api/production/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/production/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/production/totals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/production/active"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte avsluta produktion.", variant: "destructive" });
    },
  });

  const toggleExpanded = (orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const handlePrint = (orderId: string) => {
    window.open(`/production/delivery-note/${orderId}`, '_blank');
  };

  if (!production) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-6">
          <div className="text-center">
            <img src={logoUrl} alt="Rätt Order" className="h-12 w-auto object-contain mb-4 mx-auto" />
            <h1 className="text-3xl font-bold">Pakare</h1>
            <p className="text-muted-foreground mt-2">Ordrar att packa</p>
          </div>
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Ingen aktiv produktion</p>
              <p className="text-sm text-muted-foreground mt-2">
                Vänta tills en admin startar produktion
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleRefresh}
                disabled={isRefreshing}
                data-testid="button-refresh"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
                Uppdatera
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const pendingOrders = orders?.filter(o => o.status !== "packed") || [];
  const packedOrders = orders?.filter(o => o.status === "packed") || [];
  const allPacked = orders && orders.length > 0 && pendingOrders.length === 0;

  const renderOrderDetails = (order: OrderWithItems) => (
    <>
      <div className="text-sm text-muted-foreground space-y-1">
        {order.client?.phone && <p>Tel: {order.client.phone}</p>}
      </div>
      <div className="mt-3 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produkt</TableHead>
              <TableHead className="text-right">Antal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {order.items?.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.product?.name}</TableCell>
                <TableCell className="text-right">
                  {item.quantity} {getUnitLabel(item.product?.sellUnit || "pcs")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {order.client?.hasRut && order.client.rutDetails && (
        <Badge className="mt-2" variant="outline">
          Rutt: {order.client.rutDetails}
        </Badge>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="Rätt Order" className="h-10 w-auto object-contain" data-testid="img-production-logo" />
            <div>
              <h1 className="text-2xl font-bold">Pakare</h1>
              <p className="text-muted-foreground">Ordrar att packa och leverera</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Uppdatera"
              data-testid="button-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
              Produktion aktiv
            </Badge>
            <Badge variant="secondary">
              {packedOrders.length}/{orders?.length || 0} packade
            </Badge>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Ange ditt ID</CardTitle>
            <CardDescription>
              Ange ditt personal-ID för att registrera vem som packar ordrarna
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="packerId" className="sr-only">Pakare ID</Label>
                <Input
                  id="packerId"
                  placeholder="T.ex. 123 eller ditt namn"
                  value={packerId}
                  onChange={(e) => handlePackerIdChange(e.target.value)}
                  data-testid="input-packer-id"
                />
              </div>
              {packerId && (
                <Badge variant="secondary">
                  Aktiv: {packerId}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {totals && totals.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Produktstatus</h2>
            </div>
            {totals.map((item) => (
              <Card 
                key={item.productId}
                className={
                  item.bagareCompleted ? "border-green-500/50 bg-green-500/5" : 
                  item.tragareCompleted ? "border-blue-500/50 bg-blue-500/5" : 
                  ""
                }
                data-testid={`card-product-status-${item.productId}`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{item.productName}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.bagareCompleted ? "Redo för packning" : 
                         item.tragareCompleted ? "Väntar på bagare" : 
                         "Väntar på trågare"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge 
                        variant="outline" 
                        className={item.tragareCompleted ? "bg-green-500/10 text-green-600 border-green-500/20" : "bg-gray-500/10 text-gray-500 border-gray-500/20"}
                      >
                        {item.tragareCompleted ? <Check className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                        Trågare{item.tragareBy ? `: ${item.tragareBy}` : ""}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={item.bagareCompleted ? "bg-green-500/10 text-green-600 border-green-500/20" : "bg-gray-500/10 text-gray-500 border-gray-500/20"}
                      >
                        {item.bagareCompleted ? <Check className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                        Bagare{item.bagareBy ? `: ${item.bagareBy}` : ""}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {allPacked && (
          <Card className="border-green-500/50 bg-green-500/5">
            <CardContent className="py-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-semibold text-lg">Alla ordrar är packade!</p>
                    <p className="text-sm text-muted-foreground">
                      Klicka för att avsluta produktion och återställa cykeln
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={() => finishProductionMutation.mutate()}
                  disabled={finishProductionMutation.isPending}
                  data-testid="button-finish-production"
                >
                  <Flag className="h-4 w-4 mr-2" />
                  Avsluta produktion
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Ordrar att packa
            </CardTitle>
            <CardDescription>
              Markera varje order som packad när den är klar
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : pendingOrders.length > 0 ? (
              <div className="space-y-4">
                {pendingOrders.map((order) => (
                  <div
                    key={order.id}
                    className="p-4 border rounded-lg hover-elevate"
                  >
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <p className="font-semibold">{order.client?.name}</p>
                          <Badge variant="outline">
                            #{order.orderNumber}
                          </Badge>
                          {order.scheduledDelivery && order.deliveryDate && (
                            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20" data-testid={`badge-delivery-${order.id}`}>
                              <CalendarDays className="h-3 w-3 mr-1" />
                              Leverans {new Date(order.deliveryDate).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}
                            </Badge>
                          )}
                        </div>
                        {renderOrderDetails(order)}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => markPackedMutation.mutate(order.id)}
                          disabled={markPackedMutation.isPending || !packerId}
                          data-testid={`button-pack-${order.id}`}
                          title={!packerId ? "Ange pakare-ID först" : ""}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Markera packad
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handlePrint(order.id)}
                          data-testid={`button-print-${order.id}`}
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          Följesedel
                        </Button>
                      </div>
                    </div>
                    {!packerId && (
                      <p className="text-sm text-amber-600 mt-2">Ange ditt ID ovan innan du packar</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-muted-foreground">Alla ordrar är packade!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {packedOrders.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Packade ordrar
              </CardTitle>
              <CardDescription>
                Klicka på en order för att se detaljer
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {packedOrders.map((order) => (
                  <Collapsible
                    key={order.id}
                    open={expandedOrders.has(order.id)}
                    onOpenChange={() => toggleExpanded(order.id)}
                  >
                    <CollapsibleTrigger asChild>
                      <div
                        className="flex items-center justify-between p-3 bg-green-500/5 border border-green-500/20 rounded-lg cursor-pointer hover-elevate"
                        data-testid={`packed-order-${order.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                          <div>
                            <p className="font-medium flex items-center gap-2 flex-wrap">
                              {order.client?.name}
                              {order.scheduledDelivery && order.deliveryDate && (
                                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-xs">
                                  <CalendarDays className="h-3 w-3 mr-1" />
                                  Leverans {new Date(order.deliveryDate).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}
                                </Badge>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              #{order.orderNumber} - {order.items?.length} produkter
                              {order.packedBy && ` - Packad av: ${order.packedBy}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(order.id);
                            }}
                            data-testid={`button-print-packed-${order.id}`}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Badge variant="outline" className="bg-green-500/10 text-green-600">
                            Packad
                          </Badge>
                          {expandedOrders.has(order.id) ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 border border-t-0 border-green-500/20 rounded-b-lg bg-background">
                        {renderOrderDetails(order)}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
